import React, { useMemo } from 'react';
import arc from 'svg-arc';
import styled from 'styled-components';
import { TextStyles } from '@mono/metui/src/utils/text-styles-selector';

interface IDial {
    arrowValue?: number;
    dashValue?: number;
    clockWiseDeviation?: number;
    counterClockWiseDeviation?: number;
    runwayAngle?: number;
    crosswindAngle?: number;
    crosswindSpeed?: number;
    headwindAngle?: number;
    headwindSpeed?: number;
    mode?: 'cozy' | 'compact';
    width: number;
    height: number;
    theme: { bg: string; text: string; themeName?: string };
}

const offset = 90;
const quarterOffset = 16;

export const Dial = ({
    arrowValue,
    dashValue,
    clockWiseDeviation,
    counterClockWiseDeviation,
    runwayAngle,
    headwindAngle,
    headwindSpeed,
    crosswindAngle,
    crosswindSpeed,
    mode = 'cozy',
    height,
    width,
    theme,
}: IDial) => {
    const largeDial = mode === 'cozy';
    const arrowScale = largeDial ? 1.6 : 1;
    const arrowWidthOffset = largeDial ? arrowScale * 14 : 14;
    const arrowHeightOffset = largeDial ? arrowScale * 38 : 38;
    const svgHeight = height - (runwayAngle !== undefined ? 92 : 38);
    const svgWidth = width;
    const svgSize = Math.min(svgHeight, svgWidth) - offset / 2;

    const ringAdjustment = largeDial ? 0 : 8;

    const ringRadius = (svgSize - offset * 2) / 2 + offset / 2 + ringAdjustment;
    const ringPos = ringRadius + offset - quarterOffset - ringAdjustment;
    const innerSize = ringRadius - (largeDial ? 32 : 12);

    const runwayHeight = largeDial ? Math.max(ringRadius * 0.75, 63) : Math.max(ringRadius * 0.75, 52);
    const runwayWidth = largeDial ? 44 : 32;

    const StyledText = styled.text<{ type: string; mode: 'cozy' | 'compact' }>`
        ${({ type, mode }) => {
            if (type === 'runwayArrowFont') return TextStyles[mode].h2;
            if (type === 'ringArrowFont') return TextStyles[mode].h4;
            if (type === 'angleFont') return TextStyles[mode].p;
        }}
    `;

    const isDarkMode = theme?.themeName === 'dark';

    const arrowTextColor = isDarkMode ? '#172344' : '#fff';
    const ringColors = isDarkMode
        ? {
              bg: '#4e515a',
          }
        : {
              bg: '#ECECEC',
          };

    const d = useMemo(() => {
        return arc({
            x: ringPos,
            y: ringPos,
            R: ringRadius - (largeDial ? 4 : 2),
            r: ringRadius - (largeDial ? 28 : 10),
            start: counterClockWiseDeviation ?? 0,
            end: clockWiseDeviation ?? 0,
        });
    }, [counterClockWiseDeviation, clockWiseDeviation, svgHeight, svgWidth, mode]);
    return (
        <svg height={svgSize + offset / 2} width={svgSize + offset / 2}>
            <g>
                <g>
                    <circle cx={ringPos} cy={ringPos} r={ringRadius} fill={ringColors.bg} />
                    <circle cx={ringPos} cy={ringPos} r={innerSize} fill={theme?.bg} />
                    {d && <path d={d} fillRule={'evenodd'} fill='#7599FF' />}
                </g>
                <g>
                    {Array.from({ length: 360 / 10 }).map((_, i) => {
                        const angle = i * 10;
                        if (angle % 30 === 0)
                            return (
                                <g transform={`translate(${ringPos}, ${offset - quarterOffset - ringAdjustment}) rotate(${angle}, 0, ${ringRadius})`} key={i}>
                                    <line
                                        x1={0}
                                        y1={largeDial ? 30 : 10}
                                        x2={0}
                                        y2={1}
                                        stroke={theme?.text}
                                        strokeWidth={3}
                                        strokeLinecap='round'
                                        rx={14}
                                        opacity={0.2}
                                    />
                                    <StyledText
                                        mode={mode ?? 'cozy'}
                                        type='angleFont'
                                        fill={theme?.text}
                                        textAnchor='middle'
                                        dominantBaseline='middle'
                                        transform={`translate(0, ${largeDial ? 58 : 28}) rotate(${-angle})`}
                                    >
                                        {angle}
                                    </StyledText>
                                </g>
                            );
                        return (
                            <line
                                key={i}
                                x1={0}
                                y1={largeDial ? 30 : 10}
                                x2={0}
                                y2={1}
                                stroke={theme?.text}
                                strokeWidth={2}
                                strokeLinecap='round'
                                transform={`translate(${ringPos}, ${offset - quarterOffset - ringAdjustment}) rotate(${angle}, 0, ${ringRadius})`}
                                opacity={0.2}
                            />
                        );
                    })}
                </g>
                {arrowValue !== undefined && (
                    <g
                        transform={`translate(${ringPos - arrowWidthOffset}, ${
                            offset - quarterOffset - arrowHeightOffset - ringAdjustment
                        }) rotate(${arrowValue}, ${arrowWidthOffset}, ${ringRadius + arrowHeightOffset})`}
                    >
                        <path d={regularArrow} fill={theme?.text} transform={`scale(${arrowScale})`} />
                        <StyledText
                            type='ringArrowFont'
                            mode={mode ?? 'cozy'}
                            fill={arrowTextColor}
                            textAnchor='middle'
                            transform={`translate(${arrowWidthOffset}, ${arrowHeightOffset / 2}) rotate(${-arrowValue}, 0, -4)`}
                        >
                            {arrowValue + '°'}
                        </StyledText>
                    </g>
                )}
                {dashValue !== undefined && (
                    <line
                        x1={0}
                        y1={-1}
                        x2={0}
                        y2={largeDial ? 32 : 12}
                        stroke={theme?.text}
                        strokeWidth={4}
                        transform={`translate(${ringPos}, ${offset - quarterOffset - ringAdjustment}) rotate(${dashValue}, 0, ${ringRadius})`}
                    />
                )}
                {runwayAngle !== undefined && (
                    <g transform={`rotate(${runwayAngle}, ${ringPos}, ${ringPos})`}>
                        <rect
                            rx={6}
                            width={runwayWidth}
                            height={runwayHeight}
                            x={ringPos - runwayWidth / 2}
                            y={ringPos - runwayHeight / 2}
                            fill={ringColors.bg}
                        />
                    </g>
                )}
                {headwindAngle !== undefined && crosswindAngle !== undefined && (
                    <>
                        <g
                            transform={`translate(${ringPos - arrowWidthOffset}, ${ringPos - runwayHeight / 2}) rotate(${headwindAngle}, ${arrowWidthOffset}, ${
                                runwayHeight / 2
                            })`}
                        >
                            <path d={regularArrow} fill={theme?.text} transform={`scale(${arrowScale})`} />
                            <StyledText
                                type='runwayArrowFont'
                                mode={mode ?? 'cozy'}
                                fill={arrowTextColor}
                                textAnchor='middle'
                                transform={`translate(${arrowWidthOffset}, ${runwayWidth / 1.5}) rotate(${-headwindAngle}, ${largeDial ? `-2, -6` : `0, -4`})`}
                            >
                                {headwindSpeed}
                            </StyledText>
                        </g>
                        <g
                            transform={`translate(${ringPos - arrowWidthOffset}, ${
                                ringPos - (arrowHeightOffset + runwayWidth / 2)
                            }) rotate(${crosswindAngle}, ${arrowWidthOffset}, ${arrowHeightOffset + runwayWidth / 2})`}
                        >
                            <path d={regularArrow} fill={theme?.text} transform={`scale(${arrowScale})`} />
                            <StyledText
                                type='runwayArrowFont'
                                mode={mode ?? 'cozy'}
                                fill={arrowTextColor}
                                textAnchor='middle'
                                transform={`translate(${arrowWidthOffset}, ${runwayWidth / 1.5}) rotate(${-crosswindAngle}, ${largeDial ? `-2, -6` : `0, -4`})`}
                            >
                                {crosswindSpeed}
                            </StyledText>
                        </g>
                    </>
                )}
            </g>
        </svg>
    );
};

const regularArrow =
    'M0.669488 28.9543L13.1428 37.4183C13.6603 37.7695 14.3398 37.7695 14.8573 37.4183L27.3305 28.9543C27.7493 28.6701 28 28.197 28 27.6909L28 4.58029C28 2.05067 25.9493 2.17829e-06 23.4197 1.82649e-06L4.5803 -7.93524e-07C2.05067 -1.14532e-06 4.99951e-06 2.05066 4.64771e-06 4.58029L1.43369e-06 27.6909C1.36332e-06 28.197 0.250745 28.6701 0.669488 28.9543Z';
